import { cn } from 'noddi-ui-common';
import { type CarProps, formatCarLicensePlate } from 'noddi-util';
import { NavioTireSeasonChip, type NavioTireSeasonChipTranslations } from '../../CarWheel/NavioTireSeasonChip';
import type { TireSeasonInput } from '../../CarWheel/tireSeasonStyle';

type Size = 'sm' | 'md';

// The worker app's NorwegianPlateBadge: a white plate, ink border and the blue EU strip. Real-world plate colors, not
// theme tokens.
const FRAMED_SIZE = {
  sm: { plate: 'h-[22px] rounded text-xs tracking-[0.04em]', strip: 'w-3', text: 'px-1.5' },
  md: { plate: 'h-8 rounded-md text-base tracking-[0.08em]', strip: 'w-[18px]', text: 'px-2.5' }
} as const satisfies Record<Size, { plate: string; strip: string; text: string }>;

// sm in table cells and list rows, md as the plate a card or sheet is about. The season box matches the plate's
// line height so the two align on center.
const PLATE_SIZE = {
  sm: { text: 'text-sm leading-[22px] tracking-[0.06em]', gap: 'gap-2' },
  md: { text: 'text-[22px] leading-7 tracking-[0.01em]', gap: 'gap-2.5' }
} as const satisfies Record<Size, { text: string; gap: string }>;

interface Props {
  car: CarProps;
  /**
   * The season box in front of the plate. `null` (no or unknown season) keeps an empty box so plates in a column stay
   * aligned; leave it out where a row has no season at all.
   */
  season?: TireSeasonInput | null;
  size?: Size;
  /**
   * `plate`: bold, no outline. `badge`: outlined, for several plates side by side in one line.
   * `text`: inherits the surrounding text. `framed`: drawn as the physical plate, the one a card is about.
   */
  variant?: 'plate' | 'badge' | 'text' | 'framed';
  className?: string;
  /** The season box's accessible name; the customer app passes its translated labels. */
  seasonTranslations?: NavioTireSeasonChipTranslations;
}

export const NavioLicensePlate = ({
  car,
  season,
  size = 'sm',
  variant = 'plate',
  className,
  seasonTranslations
}: Props) => {
  const plateText = formatCarLicensePlate(car);
  const seasonBox =
    season === undefined ? null : (
      <NavioTireSeasonChip season={season} size={size} iconOnly placeholder translations={seasonTranslations} />
    );

  if (!plateText) {
    return seasonBox;
  }

  if (variant === 'framed') {
    return (
      <span className={cn('inline-flex items-center', PLATE_SIZE[size].gap, className)}>
        {seasonBox}
        <span
          className={cn(
            'inline-flex items-stretch overflow-hidden border-[1.5px] border-[#14110D] bg-white font-bold whitespace-nowrap text-[#14110D] tabular-nums',
            FRAMED_SIZE[size].plate
          )}
        >
          <span aria-hidden className={cn('bg-[#003399]', FRAMED_SIZE[size].strip)} />
          <span className={cn('flex items-center', FRAMED_SIZE[size].text)}>{plateText}</span>
        </span>
      </span>
    );
  }

  return (
    <span
      className={cn(
        'whitespace-nowrap tabular-nums',
        variant === 'plate' && cn('inline-flex items-center font-bold', PLATE_SIZE[size].text, PLATE_SIZE[size].gap),
        variant === 'badge' &&
          'inline-flex items-center justify-center rounded-md border border-border bg-background px-2 py-0.5 text-sm font-semibold tracking-wider',
        className
      )}
    >
      {seasonBox}
      {plateText}
    </span>
  );
};
